import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  CircularProgress,
  Divider,
  Alert,
  Paper
} from '@mui/material';
// Remove direct Firestore imports - use service layer instead

const AssignFormModal = ({ open, onClose, form }) => {
  const [loading, setLoading] = useState(true);
  const [facilityGroups, setFacilityGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching data for form:', form.id);

      // TODO: Replace direct Firestore access with service layer calls
      // Example: 
      // const groupsData = await facilityService.getAllFacilityGroups();
      // const facilitiesData = await facilityService.getAllFacilities();
      // const formData = await formService.getForm(form.id);
      
      console.warn('AssignFormModal: Direct Firestore access removed - implement service layer calls');
      
      // For now, set empty data until service layer is implemented
      setFacilityGroups([]);
      setFacilities([]);
      setSelectedGroups([]);
      setSelectedFacilities([]);
      
      /* Original code removed:
      // First fetch all facility groups
      const groupsSnapshot = await getDocs(collection(db, 'facilityGroups'));
      const groupsData = groupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('All groups:', groupsData);

      // Fetch all facilities
      const facilitiesSnapshot = await getDocs(collection(db, 'facilities'));
      const facilitiesData = facilitiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('All facilities:', facilitiesData);

      // Get currently assigned groups and facilities from the form document
      const formDoc = await getDoc(doc(db, 'forms', form.id));
      const formData = formDoc.data();
      const assignments = formData.assignments || {
        facilityGroups: [],
        facilities: [],
        updatedAt: null
      };

      setFacilityGroups(groupsData);
      setFacilities(facilitiesData);
      setSelectedGroups(assignments.facilityGroups || []);
      setSelectedFacilities(assignments.facilities || []);
      */

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load facilities and groups');
    } finally {
      setLoading(false);
    }
  }, [form.id]);

  useEffect(() => {
    if (open && form?.id) {
      fetchData();
    }
  }, [open, form, fetchData]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Saving assignments:', { selectedGroups, selectedFacilities });

      // TODO: Replace direct Firestore access with service layer call
      // Example: await formService.updateFormAssignments(form.id, { selectedGroups, selectedFacilities });
      
      console.warn('AssignFormModal: Direct Firestore access removed - implement service layer call');
      
      console.log('Save completed successfully');
      onClose(true);
      
      /* Original code removed:
      // Update form document with assignments
      const formRef = doc(db, 'forms', form.id);
      await updateDoc(formRef, {
        assignments: {
          facilityGroups: selectedGroups,
          facilities: selectedFacilities,
          updatedAt: new Date()
        }
      });

      console.log('Save completed successfully');
      onClose(true);
      */
    } catch (err) {
      console.error('Error saving assignments:', err);
      setError('Failed to save assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleGroupToggle = (groupId) => {
    console.log('Toggling group:', groupId);
    setSelectedGroups(prev => {
      const newSelection = prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId];
      
      // If group is selected, remove its facilities from individual selection
      if (newSelection.includes(groupId)) {
        const group = facilityGroups.find(g => g.id === groupId);
        if (group?.facilities) {
          setSelectedFacilities(prev => 
            prev.filter(id => !group.facilities.includes(id))
          );
        }
      }
      
      return newSelection;
    });
  };

  const handleFacilityToggle = (facilityId) => {
    console.log('Toggling facility:', facilityId);
    // Check if facility belongs to any selected group
    const belongsToSelectedGroup = facilityGroups.some(group => 
      selectedGroups.includes(group.id) && 
      group.facilities?.includes(facilityId)
    );

    if (belongsToSelectedGroup) {
      console.log('Facility belongs to selected group, ignoring toggle');
      return;
    }

    setSelectedFacilities(prev => 
      prev.includes(facilityId)
        ? prev.filter(id => id !== facilityId)
        : [...prev, facilityId]
    );
  };

  const isFacilityDisabled = (facilityId) => {
    return facilityGroups.some(group => 
      selectedGroups.includes(group.id) && 
      group.facilities?.includes(facilityId)
    );
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        Assign Form: {form?.name}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  About Facility Group Assignment
                </Typography>
                When you assign a form to a facility group:
                <ul>
                  <li>The form will be automatically assigned to all current facilities in the group</li>
                  <li>Any new facilities added to this group in the future will automatically get this form</li>
                  <li>Individual facility assignments within a group cannot be modified</li>
                </ul>
              </Alert>
            </Paper>

            <Typography variant="h6" gutterBottom>
              Facility Groups
            </Typography>
            <List>
              {facilityGroups.map((group) => (
                <ListItem
                  key={group.id}
                  button
                  onClick={() => handleGroupToggle(group.id)}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedGroups.includes(group.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={group.name}
                    secondary={
                      <>
                        {`${group.facilities?.length || 0} facilities`}
                        {selectedGroups.includes(group.id) && 
                          " • All current and future facilities in this group will be assigned"}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Individual Facilities
            </Typography>
            <List>
              {facilities.map((facility) => {
                const disabled = isFacilityDisabled(facility.id);
                const groupName = facilityGroups.find(
                  g => g.facilities?.includes(facility.id) && selectedGroups.includes(g.id)
                )?.name;

                return (
                  <ListItem
                    key={facility.id}
                    button
                    onClick={() => handleFacilityToggle(facility.id)}
                    disabled={disabled}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedFacilities.includes(facility.id) || disabled}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={facility.name}
                      secondary={disabled ? `Assigned via group: ${groupName}` : facility.location}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignFormModal; 